import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronRight, Layers, MapPin } from "lucide-react";
import { UserAvatar } from "./ui/user-avatar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ChipGroup } from "./ui/chip-group";
import { Stepper } from "./ui/stepper";
import { SectionLabel } from "./ui/section-label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { STATUS_LABELS, BEMIDDELING_LABELS, BEMIDDELING_OPTIONS, statusPillKind, priorityPillKind, bemiddelingPillKind } from "./VacatureBadges";
import { pillColorClasses } from "./ui/pill";
import { ROLE_LABELS } from "../lib/roles";
import { toast } from "../lib/toast";
import { cn } from "@/lib/utils";
import { useAuth } from "../hooks/useAuth";
import { createVacature, updateVacature, Vacature, FunctionTitleSuggestion } from "../api/api";
import { useBranches, useApplicableFunctionTitleSuggestions, useOrgContacts, usePartners, useKams, useDepartmentSuggestions } from "../hooks/queries";
import { DISCIPLINES, ERVARING_OPTIONS, PRIORITY_OPTIONS, SALARY_TYPE_OPTIONS } from "../constants";
import { Popover, PopoverAnchor, PopoverContent } from "./ui/popover";

interface Props {
  partnerId: number;
  onClose: () => void;
  onCreated: () => void;
  initialData?: Vacature;
  // Vestiging to pre-select when creating (e.g. the branch tab it was opened from).
  defaultBranchId?: number | null;
}

export default function NieuweVacatureModal({ partnerId, onClose, onCreated, initialData, defaultBranchId }: Props) {
  const { user } = useAuth();
  const { data: branches } = useBranches(partnerId);
  const availableBranches = user?.role === "hmp"
    ? (branches ?? []).filter(b => user.branch_permissions.includes(b.id))
    : (branches ?? []);
  const isEdit = !!initialData;

  const { data: partners } = usePartners();
  const currentPartner = partners?.find(p => p.id === partnerId);
  const { data: kams } = useKams();
  const partnerKams = (kams ?? []).filter(k => currentPartner?.kam_ids.includes(k.id));

  const [titel, setTitel] = useState(initialData?.functie ?? "");
  const [discs, setDiscs] = useState<string[]>(initialData?.disciplines ?? []);
  const [ervaring, setErvaring] = useState(initialData?.ervaring ?? "");
  const [locatie, setLocatie] = useState(initialData?.locatie ?? "");
  const [branchId, setBranchId] = useState(initialData?.branch_id ? String(initialData.branch_id) : "");
  const [priority, setPriority] = useState(initialData?.priority ?? "normaal");
  const [status, setStatus] = useState(initialData?.status ?? "open");
  const [bemiddeling, setBemiddeling] = useState(initialData?.bemiddeling_status ?? "geen");
  // A new vacature created by an hmp defaults to themselves as hiring manager;
  // it's free text, so they can overwrite it or pick another org contact.
  const [hiringManager, setHiringManager] = useState(
    initialData?.hiring_manager ?? (user?.role === "hmp" ? user.name : "")
  );
  const [contactSuggestionsOpen, setContactSuggestionsOpen] = useState(false);
  const [department, setDepartment] = useState(initialData?.department ?? "");
  // Once the user edits the department themselves, autofill stops overwriting it.
  const [departmentTouched, setDepartmentTouched] = useState(false);
  const [aantalPosities, setAantalPosities] = useState(String(initialData?.aantal_posities ?? 1));
  const [opmerkingen, setOpmerkingen] = useState(initialData?.opmerkingen ?? "");
  const [opmerkingenOpen, setOpmerkingenOpen] = useState(false);
  const [salaryType, setSalaryType] = useState<"" | "range" | "fixed" | "other">(
    (initialData?.salary_type as "" | "range" | "fixed" | "other" | undefined) ?? ""
  );
  const [salaryMin, setSalaryMin] = useState(initialData?.salary_min != null ? String(initialData.salary_min) : "");
  const [salaryMax, setSalaryMax] = useState(initialData?.salary_max != null ? String(initialData.salary_max) : "");
  const [salaryFixed, setSalaryFixed] = useState(initialData?.salary_fixed != null ? String(initialData.salary_fixed) : "");
  const [salaryOther, setSalaryOther] = useState(initialData?.salary_other ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { data: suggestions = [] } = useApplicableFunctionTitleSuggestions(partnerId, !isEdit);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const isWavesUser = user?.role === "admin" || user?.role === "kam";
  const { data: orgContacts } = useOrgContacts(!!user, partnerId);
  // Focusing the field lists every hiring manager of the partner; only text
  // typed since then filters the list.
  const [contactFilterActive, setContactFilterActive] = useState(false);
  const contactInputRef = useRef<HTMLDivElement>(null);
  const contactQuery = hiringManager.trim().toLowerCase();
  const filteredContacts = (orgContacts ?? []).filter(c =>
    c.role !== "admin" && (!contactFilterActive || c.name.toLowerCase().includes(contactQuery))
  );
  function openContactSuggestions() {
    setContactFilterActive(false);
    setContactSuggestionsOpen(true);
  }
  const [touched, setTouched] = useState({
    priority: false, ervaring: false, discipline: false,
    aantalPosities: false, opmerkingen: false, salary: false,
  });

  // Department suggestions follow the vestiging and hiring manager. The
  // hiring manager is debounced since it can be typed freely.
  const [suggestionContact, setSuggestionContact] = useState(hiringManager.trim());
  useEffect(() => {
    const t = setTimeout(() => setSuggestionContact(hiringManager.trim()), 400);
    return () => clearTimeout(t);
  }, [hiringManager]);
  const { data: departmentSuggestion } = useDepartmentSuggestions(branchId ? Number(branchId) : undefined, suggestionContact);
  const suggestedDepartments = departmentSuggestion?.departments ?? [];

  // New vacature only: autofill the first suggested department (or clear a
  // previous autofill when the new vestiging/contact has none), unless the
  // user has already typed their own.
  useEffect(() => {
    if (isEdit || departmentTouched || !departmentSuggestion) return;
    setDepartment(departmentSuggestion.departments[0] ?? "");
  }, [isEdit, departmentTouched, departmentSuggestion]);

  const filteredSuggestions = titel.trim()
    ? suggestions.filter(s => s.title.toLowerCase().includes(titel.trim().toLowerCase()))
    : suggestions;

  function applySuggestion(s: FunctionTitleSuggestion) {
    setTitel(s.title);
    if (s.priority !== null && !touched.priority) setPriority(s.priority);
    if (s.experience !== null && !touched.ervaring) setErvaring(s.experience);
    if (s.disciplines !== null && s.disciplines.length > 0 && !touched.discipline) setDiscs(s.disciplines);
    if (s.aantal_posities !== null && !touched.aantalPosities) setAantalPosities(String(s.aantal_posities));
    if (s.opmerkingen !== null && !touched.opmerkingen) setOpmerkingen(s.opmerkingen);
    if (s.salary_type !== null && !touched.salary) {
      setSalaryType(s.salary_type as "" | "range" | "fixed" | "other");
      setSalaryMin(s.salary_min != null ? String(s.salary_min) : "");
      setSalaryMax(s.salary_max != null ? String(s.salary_max) : "");
      setSalaryFixed(s.salary_fixed != null ? String(s.salary_fixed) : "");
      setSalaryOther(s.salary_other ?? "");
    }
    setSuggestionsOpen(false);
  }

  function toggleDisc(d: string) {
    setDiscs(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    setTouched(t => ({ ...t, discipline: true }));
  }

  function setSalaryMode(mode: "" | "range" | "fixed" | "other") {
    setSalaryType(mode);
    setTouched(t => ({ ...t, salary: true }));
  }

  async function handlePublish() {
    if (!titel.trim()) { setError("Vul een functietitel in"); return; }
    if (!branchId) { setError("Selecteer een vestiging"); return; }
    if (salaryType === "range" && (!salaryMin || !salaryMax)) { setError("Vul zowel het minimum- als maximumsalaris in"); return; }
    if (salaryType === "range" && salaryMin && salaryMax && Number(salaryMin) > Number(salaryMax)) { setError("Minimumsalaris mag niet hoger zijn dan maximumsalaris"); return; }
    if (salaryType === "range" && (!Number.isInteger(Number(salaryMin)) || !Number.isInteger(Number(salaryMax)) || Number(salaryMin) < 0 || Number(salaryMax) < 0)) { setError("Salaris moet een geheel, positief bedrag zijn"); return; }
    if (salaryType === "fixed" && !salaryFixed) { setError("Vul een bedrag in"); return; }
    if (salaryType === "fixed" && (!Number.isInteger(Number(salaryFixed)) || Number(salaryFixed) < 0)) { setError("Salaris moet een geheel, positief bedrag zijn"); return; }
    if (salaryType === "other" && !salaryOther.trim()) { setError("Vul een salarisomschrijving in"); return; }
    const salaryPayload = {
      salary_type: salaryType,
      salary_min: salaryType === "range" ? Number(salaryMin) : null,
      salary_max: salaryType === "range" ? Number(salaryMax) : null,
      salary_fixed: salaryType === "fixed" ? Number(salaryFixed) : null,
      salary_other: salaryType === "other" ? salaryOther.trim() : null,
    };
    const includeSalary = touched.salary || initialData?.salary_type != null;
    setSaving(true); setError("");
    try {
      if (isEdit) {
        await updateVacature(initialData!.id, { functie: titel.trim(), status, bemiddeling_status: bemiddeling, branch_id: Number(branchId), priority, hiring_manager: hiringManager.trim(), department: department.trim() || null, locatie: locatie.trim(), ervaring, disciplines: discs, aantal_posities: Number(aantalPosities) || 1, opmerkingen: opmerkingen.trim(), ...(includeSalary ? salaryPayload : {}) }, { silenceErrors: true });
      } else {
        await createVacature({ functie: titel.trim(), partner_id: partnerId, branch_id: Number(branchId), priority, contactpersoon: user?.name ?? "", hiring_manager: hiringManager.trim(), department: department.trim() || null, locatie: locatie.trim(), ervaring, disciplines: discs, aantal_posities: Number(aantalPosities) || 1, opmerkingen: opmerkingen.trim(), ...salaryPayload }, { silenceErrors: true });
      }
      toast(isEdit ? "Wijzigingen opgeslagen" : "Vacature gepubliceerd", { kind: "success" });
      onCreated(); onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Fout bij opslaan"); setSaving(false); }
  }

  const selectedBranch = availableBranches.find(t => String(t.id) === branchId);

  function handleBranchChange(id: string) {
    setBranchId(id);
    const branch = availableBranches.find(t => String(t.id) === id);
    if (branch) setLocatie(branch.location);
  }

  // New vacature only: once branches have loaded, pre-select the requested
  // vestiging if the user may use it, otherwise the only one they have access to.
  useEffect(() => {
    if (isEdit || branchId || availableBranches.length === 0) return;
    const preferred = availableBranches.find(b => b.id === defaultBranchId)
      ?? (availableBranches.length === 1 ? availableBranches[0] : undefined);
    if (preferred) handleBranchChange(String(preferred.id));
  }, [isEdit, branches, defaultBranchId]);

  const NONE = "__none";

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent data-tour="edit-form" className="flex max-h-[calc(100dvh-2rem)] max-w-[820px] flex-col gap-0 overflow-hidden rounded-[28px] p-0">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-1 border-b border-waves-line px-6 pb-5 pt-8 pr-20 md:px-11 md:pt-9">
          <DialogTitle className="text-[26px]">{isEdit ? "Vacature bewerken" : "Nieuwe vacature"}</DialogTitle>
          <DialogDescription className="text-sm text-waves-muted">
            {selectedBranch ? `${selectedBranch.name} · ${selectedBranch.location}` : "Selecteer een vestiging"}
          </DialogDescription>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-5 overflow-auto px-6 py-6 md:px-11">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <Field tour="edit-title" label="Functietitel" required hint={!titel && !isEdit && suggestions.length > 0 ? "Suggesties beschikbaar" : undefined}>
                <Popover open={!isEdit && suggestionsOpen && filteredSuggestions.length > 0} onOpenChange={setSuggestionsOpen}>
                  <PopoverAnchor asChild>
                    <div>
                      <Input
                        value={titel}
                        onChange={e => { setTitel(e.target.value); setSuggestionsOpen(true); }}
                        onFocus={() => setSuggestionsOpen(true)}
                        placeholder="Vul een functietitel in…"
                      />
                    </div>
                  </PopoverAnchor>
                  <PopoverContent align="start" onOpenAutoFocus={e => e.preventDefault()} className="w-[--radix-popover-trigger-width] p-1.5">
                    <div className="flex max-h-56 flex-col overflow-y-auto">
                      {filteredSuggestions.map(s => (
                        <button key={s.id} type="button" onClick={() => applySuggestion(s)} className="rounded-lg px-3 py-2 text-left text-[15px] hover:bg-waves-mist">
                          {s.title}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </Field>
            </div>
            <Field tour="edit-vest" label="Vestiging" required>
              <Select value={branchId || undefined} onValueChange={handleBranchChange}>
                <SelectTrigger className={cn(!branchId && "text-waves-muted")}><SelectValue placeholder="Kies vestiging" /></SelectTrigger>
                <SelectContent>
                  {availableBranches.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field tour="edit-disc" label="Discipline" optional>
            <ChipGroup
              multiple
              aria-label="Discipline"
              value={discs}
              onChange={v => { setDiscs(v); setTouched(t => ({ ...t, discipline: true })); }}
              options={DISCIPLINES.map(d => ({ value: d, label: d }))}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field tour="edit-erv" label="Ervaring" optional>
              <Select value={ervaring || NONE} onValueChange={v => { setErvaring(v === NONE ? "" : v); setTouched(t => ({ ...t, ervaring: true })); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Niet opgegeven</SelectItem>
                  {ERVARING_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Locatie" optional>
              <div className="relative">
                <MapPin size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-waves-muted" />
                <Input value={locatie} onChange={e => setLocatie(e.target.value)} placeholder={selectedBranch?.location || "Locatie…"} className="pl-10" />
              </div>
            </Field>
            <Field tour="edit-afd" label="Afdeling" optional>
              <div className="relative">
                <Layers size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-waves-muted" />
                <Input value={department} onChange={e => { setDepartment(e.target.value); setDepartmentTouched(true); }} placeholder="Afdeling…" className="pl-10" />
              </div>
              {suggestedDepartments.filter(d => d !== department.trim()).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {suggestedDepartments.filter(d => d !== department.trim()).map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => { setDepartment(d); setDepartmentTouched(true); }}
                      className="rounded-pill border border-waves-line bg-white px-2.5 py-0.5 text-xs text-waves-body transition-colors hover:bg-waves-mist"
                    >
                      {d}
                    </button>
                  ))}
                </div>
              )}
            </Field>
          </div>

          <div className={cn("grid grid-cols-1 gap-4", isEdit ? "md:grid-cols-3" : "md:grid-cols-2")}>
            <Field tour="edit-prio" label="Prioriteit">
              <ChipGroup
                aria-label="Prioriteit"
                value={priority}
                onChange={v => { setPriority(v); setTouched(t => ({ ...t, priority: true })); }}
                options={PRIORITY_OPTIONS.map(o => ({ value: o.value, label: o.label, activeClassName: cn("border-transparent", pillColorClasses(priorityPillKind(o.value))) }))}
              />
            </Field>
            {isEdit && (
              <Field tour="edit-status" label="Status">
                <ChipGroup
                  aria-label="Status"
                  value={status}
                  onChange={setStatus}
                  options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label, activeClassName: cn("border-transparent", pillColorClasses(statusPillKind(value))) }))}
                />
              </Field>
            )}
            <Field tour="edit-spots" label="Aantal posities">
              <Stepper
                aria-label="Aantal posities"
                value={Number(aantalPosities) || 1}
                min={1}
                max={99}
                onChange={n => { setAantalPosities(String(n)); setTouched(t => ({ ...t, aantalPosities: true })); }}
              />
            </Field>
          </div>

          {isEdit && (
            <Field tour="edit-bem" label="Bemiddeling">
              <ChipGroup
                aria-label="Bemiddeling"
                value={bemiddeling}
                onChange={setBemiddeling}
                options={BEMIDDELING_OPTIONS.map(value => ({ value, label: BEMIDDELING_LABELS[value], activeClassName: cn("border-transparent", pillColorClasses(bemiddelingPillKind(value))) }))}
              />
            </Field>
          )}

          <Field tour="edit-sal" label="Salarisindicatie" optional>
            <ChipGroup
              aria-label="Salaristype"
              value={salaryType || null}
              onChange={v => setSalaryMode(salaryType === v ? "" : (v as "range" | "fixed" | "other"))}
              options={SALARY_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            />
            {salaryType === "range" && (
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="1" value={salaryMin} onChange={e => { setSalaryMin(e.target.value); setTouched(t => ({ ...t, salary: true })); }} placeholder="Van (€)" />
                <span className="shrink-0 text-sm text-waves-muted">tot</span>
                <Input type="number" min="0" step="1" value={salaryMax} onChange={e => { setSalaryMax(e.target.value); setTouched(t => ({ ...t, salary: true })); }} placeholder="Tot (€)" />
                <span className="shrink-0 text-sm text-waves-muted">/ mnd</span>
              </div>
            )}
            {salaryType === "fixed" && (
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="1" value={salaryFixed} onChange={e => { setSalaryFixed(e.target.value); setTouched(t => ({ ...t, salary: true })); }} placeholder="Bedrag (€)" />
                <span className="shrink-0 text-sm text-waves-muted">/ mnd</span>
              </div>
            )}
            {salaryType === "other" && (
              <Input value={salaryOther} onChange={e => { setSalaryOther(e.target.value); setTouched(t => ({ ...t, salary: true })); }} placeholder="Bijvoorbeeld: conform cao, in overleg…" />
            )}
          </Field>

          <div data-tour="edit-note" className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setOpmerkingenOpen(o => !o)}
              aria-expanded={opmerkingenOpen}
              className="flex items-baseline gap-1.5 text-left"
            >
              <ChevronRight size={14} className={cn("self-center text-waves-muted transition-transform", opmerkingenOpen && "rotate-90")} />
              <span className="text-[13px] font-bold">Omschrijving / opmerkingen</span>
              <span className="ml-auto text-xs text-waves-muted">optioneel</span>
            </button>
            {opmerkingenOpen ? (
              <Textarea
                rows={4}
                autoFocus
                value={opmerkingen}
                onChange={e => { setOpmerkingen(e.target.value); setTouched(t => ({ ...t, opmerkingen: true })); }}
                placeholder="Extra context, vereisten of opmerkingen…"
              />
            ) : opmerkingen.trim() && (
              <button type="button" onClick={() => setOpmerkingenOpen(true)} className="truncate text-left text-sm text-waves-muted">
                {opmerkingen.trim()}
              </button>
            )}
          </div>

          <Field label="Hiring manager" optional>
            <Popover open={contactSuggestionsOpen && filteredContacts.length > 0} onOpenChange={setContactSuggestionsOpen}>
              <PopoverAnchor asChild>
                <div ref={contactInputRef}>
                  <Input
                    value={hiringManager}
                    onChange={e => { setHiringManager(e.target.value); setContactFilterActive(true); setContactSuggestionsOpen(true); }}
                    onFocus={openContactSuggestions}
                    onClick={openContactSuggestions}
                    placeholder={isWavesUser ? "Hiring manager" : user?.name ?? "Naam hiring manager…"}
                  />
                </div>
              </PopoverAnchor>
              <PopoverContent
                align="start"
                onOpenAutoFocus={e => e.preventDefault()}
                // Clicking the input itself must not count as "outside" and close the list.
                onInteractOutside={e => { if (contactInputRef.current?.contains(e.target as Node)) e.preventDefault(); }}
                className="w-[--radix-popover-trigger-width] p-1.5"
              >
                <div className="flex max-h-56 flex-col overflow-y-auto">
                  {filteredContacts.map(c => (
                    <button key={c.id} type="button" onClick={() => { setHiringManager(c.name); setContactSuggestionsOpen(false); }} className="rounded-lg px-3 py-2 text-left text-[15px] hover:bg-waves-mist">
                      {c.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <SectionLabel>Aangemaakt door</SectionLabel>
              <PersonChip name={isEdit ? initialData!.created_by : user?.name ?? user?.email ?? "—"} sub={isEdit ? undefined : ROLE_LABELS[user?.role ?? ""] ?? user?.role} />
            </div>
            <div className="flex flex-col gap-2">
              <SectionLabel>Contactpersoon Waves</SectionLabel>
              {partnerKams.length > 0
                ? partnerKams.map(kam => <PersonChip key={kam.id} name={kam.name} sub={kam.email} />)
                : <p className="text-sm text-waves-muted">Geen key account manager toegewezen.</p>}
            </div>
          </div>
        </div>

        {error && (
          <div className="shrink-0 px-6 pb-3 md:px-11">
            <div role="alert" className="rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">{error}</div>
          </div>
        )}

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-waves-line px-6 py-5 md:px-11">
          <span className="flex-1 text-[13px] text-waves-muted">Velden met <span className="font-bold text-waves-koraal-hover">*</span> zijn verplicht</span>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button data-tour="edit-save" onClick={handlePublish} disabled={saving}>
            <Check /> {saving ? "Opslaan…" : isEdit ? "Wijzigingen opslaan" : "Publiceren"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, optional, hint, tour, children }: { label: string; required?: boolean; optional?: boolean; hint?: string; tour?: string; children: React.ReactNode }) {
  return (
    <div data-tour={tour} className="flex flex-col gap-2">
      <div className="flex items-baseline gap-1.5">
        <span className="text-[13px] font-bold">{label}</span>
        {required && <span className="font-bold text-waves-koraal-hover">*</span>}
        {(optional || hint) && <span className="ml-auto text-xs text-waves-muted">{hint ?? "optioneel"}</span>}
      </div>
      {children}
    </div>
  );
}

function PersonChip({ name, sub }: { name: string; sub?: string }) {
  return (
    <div className="flex items-center gap-3 rounded-card border border-waves-line bg-waves-subtle p-3">
      <UserAvatar name={name} size={36} />
      <div className="min-w-0">
        <div className="truncate text-sm font-bold">{name}</div>
        {sub && <div className="truncate text-xs text-waves-muted">{sub}</div>}
      </div>
    </div>
  );
}
